import { FC, createContext, useContext, useState, useMemo, useEffect, Dispatch, SetStateAction} from "react";
import { useQuery } from "react-query";

import { WithChildren } from "../../../../../../../../_metronic/helpers";

import { IDOCLabelApplication, initDOCLabelApplicationValues } from "../core";
import { getDLMTypeById } from "../../../../../../core/doclabel";

import { filter, cloneDeep, orderBy, findIndex } from "lodash";

type DLMLabelSetOption = {
  value: number
  text: string
  hourRating: string
  threshold: number
}

type DOCLabelFormViewContextProps = {
  initValues : IDOCLabelApplication
  setInitValues: Dispatch<SetStateAction<IDOCLabelApplication>>
  submitFn: Function
  onSuccess: Function
  cancelForm: Function
  appType: string
  units: Array<string>
  isDoor: boolean
  dlmTypeCode: string
  labelSetOptions: Array<DLMLabelSetOption>
}

const initialDOCLabelFormView: DOCLabelFormViewContextProps = {
  initValues: cloneDeep(initDOCLabelApplicationValues),
  setInitValues: () => {},
  submitFn: () => {},
  onSuccess: () => {},
  cancelForm: () => {},
  appType: '',
  units: [],
  isDoor: false,
  dlmTypeCode: '',
  labelSetOptions: [],
}

type DOCLabelFormViewProviderProps = {
  initData : IDOCLabelApplication
  submitFn: Function
  onSuccess: Function
  cancelForm: Function
}

const DOCLabelFormViewContext = createContext<DOCLabelFormViewContextProps>(initialDOCLabelFormView);

const DOCLabelFormViewProvider: FC<DOCLabelFormViewProviderProps & WithChildren> = ({initData, submitFn,  onSuccess, cancelForm, children}) => {

  const [initValues, setInitValues] = useState({...initDOCLabelApplicationValues});
  const { appType, dlmTypeId, certID } = initData;

  const { data: dlmtype_data, isLoading: dlmIsLoading, isError: dlmIsError } = useQuery(
    ['DLMTYPE-VIEW', dlmTypeId, certID ],
    () => getDLMTypeById(dlmTypeId, certID),
    {
      enabled: (dlmTypeId > 0 && certID > 0),
      cacheTime: 0,
      keepPreviousData: true,
      refetchOnWindowFocus: false
    }
  )

  // obtain info from dlmType definition
  const {units, isDoor, dlmTypeCode, labelSets, inspectionLevel} = useMemo(() => {
    if (! (dlmtype_data && !(dlmIsLoading || dlmIsError))) {
      return { units : [], isDoor : false, dlmTypeCode: '', labelSets: [], inspectionLevel : 0} ;
    }

    const { typeUnits, door, type: dlmTypeCode, labelSets, inspectionLevel: inspectValue, mandatory } = dlmtype_data;

    // set inspectionLevel to 0 when mandatory DOC
    let inspectionLevel = (dlmTypeCode === 'TYPE_DOC' && mandatory) ? 0 : inspectValue;

    const units = (typeUnits && typeUnits.length > 0) ?  filter(typeUnits, (o) => (! o.deleted)).map( o => o.name).sort() : [];
    const isDoor = door ? true : false;

    return {
      units,
      isDoor,
      dlmTypeCode,
      labelSets : labelSets && labelSets.length > 0 ? filter(labelSets, (o) => (! o.deleted)) : [],
      inspectionLevel: inspectionLevel !== undefined ? inspectionLevel : 0
    };

  }, [dlmtype_data, dlmIsLoading, dlmIsError]);


  const labelSetOptions = useMemo(() => {
    if (! (labelSets && labelSets.length > 0)) return [];
    const labelSetOptions = orderBy(filter(labelSets, (o:any) => (! o?.deleted)).map(o => { return {
      value: o.id,
      text: o.hourRating,
      hourRating: o.hourRating,
      threshold: (o?.threshold && o.threshold > 0) ? o.threshold : 0
    }}), ['hourRating'], ['asc']);
    return labelSetOptions;
  }, [labelSets]);

  useEffect(() => {

    let _init = {
      ...initDOCLabelApplicationValues,
      ...cloneDeep(initData),
      inspectionLevel,
      dlmTypeCode,
      confirm: false
    };

    const isDraft = initData?.isDraft ? true : false;

    // prepopulate site details
    if (! (_init?.siteDetails && _init.siteDetails.length > 0)) {
      //@ts-ignore
      const _siteDetails = cloneDeep(_init.siteDetails[0]);
      Object.assign(_init, { siteDetails: [_siteDetails]});
    }
    // auto set label set values
    if (labelSetOptions && labelSetOptions.length > 0) {
      if (_init.requestMode === 'resubmit' && _init.labelSet) {
        // reinstate threshold values for resubmit
        const _idx = findIndex(labelSetOptions, { value: _init.labelSet});
        if (_idx > -1) {
          Object.assign(_init, {
            threshold: labelSetOptions[_idx].threshold + ((dlmTypeCode === 'TYPE_DOC' && initData?.coverageArea) ? initData.coverageArea : 0) + ((dlmTypeCode === 'TYPE_LABEL' && initData?.totalLabelQuantity) ? initData.totalLabelQuantity : 0)
          });
        }
      } else if (labelSetOptions.length === 1) {
        Object.assign(_init, {
          labelSet: labelSetOptions[0].value,
          hourRating: labelSetOptions[0].hourRating,
          threshold: labelSetOptions[0].threshold + (
            isDraft ?
            0
            :
            ((dlmTypeCode === 'TYPE_DOC' && initData?.coverageArea) ? initData.coverageArea : 0) + ((dlmTypeCode === 'TYPE_LABEL' && initData?.totalLabelQuantity) ? initData.totalLabelQuantity : 0)
          )
        });
      }
    }
    setInitValues(_init);
  },[initData, inspectionLevel, labelSetOptions, dlmTypeCode]);

  return (
    <DOCLabelFormViewContext.Provider value={{
      appType,
      initValues,
      setInitValues,
      submitFn,
      onSuccess,
      cancelForm,
      units,
      isDoor,
      dlmTypeCode,
      labelSetOptions
    }}>
      {children}
    </DOCLabelFormViewContext.Provider>
  )
}

const useDOCLabelFormView = () => useContext(DOCLabelFormViewContext);
export {DOCLabelFormViewProvider, useDOCLabelFormView}